class Header {
    /**
     * @param root {Element|HTMLElement}
     */
    constructor(root) {
        this.root = root

        this.initializeObserverDelay = 50
        this.initializeObserverRetryCount = 0

        this.isDesktop = window.matchMedia('(min-width: 1024px)')

        this.initializeElements()
        this.initializeEvents()
        this.initializeIntersectionObserver()
    }

    initializeElements() {
        this.menu = this.root.querySelector('[data-sub-component="menu"]')
        this.mainMenu = this.root.querySelector('[data-component="mainMenu"]')
        this.metaMenu = this.root.querySelector('[data-component-part="metaMenu"]')
        this.languageMenu = this.root.querySelector('[data-component-part="languageMenu"]')
        this.toggle = this.root.querySelector('[data-sub-component="menuToggle"]')
        this.inner = this.root.querySelector('[data-component-part="inner"]')
        this.scrollDetector = document.querySelector('[data-component="scrollDetector"]')
        this.headerSearch = this.root.querySelector('[data-component="headerSearch"]')
    }

    initializeEvents() {
        this.toggle.addEventListener('click', () => {
            if (this.isOpen()) {
                this.close()
            } else {
                this.open()
            }
        })
        this.root.addEventListener('click', ev => {
            // Avoid click bubble up
            ev.stopPropagation()
        })

        this.root.addEventListener('transitionend', ev => {
            if (ev.currentTarget === ev.target) {
                this.root.classList.remove('is-animated')
            }
        })

        document.addEventListener('click', () => {
            this.close()
        })

        this.isDesktop.addEventListener('change', () => {
            this.initializeIntersectionObserver()
        })

        this.mainMenu.addEventListener('subMenuOpened', () => {
            this.root.classList.add('is-sub-menu-open')
        })

        this.mainMenu.addEventListener('subMenuClosed', () => {
            this.root.classList.remove('is-sub-menu-open')
        })
    }

    initializeIntersectionObserver() {
        if (this.intersectionObserver) {
            // Cancel requested animation frames to avoid overlapping calculations
            this.requestedAnimationFrames.forEach(handle => {
                window.cancelAnimationFrame(handle)
            })
            this.intersectionObserver.disconnect()
            delete this.intersectionObserver
        }

        const innerHeaderHeight = this.inner.getBoundingClientRect().height
        const detectorHeight = this.scrollDetector.getBoundingClientRect().height

        if (detectorHeight === 0) {
            if (this.initializeObserverRetryCount > 20) {
                // Avoid endless retries
                return
            }

            this.initializeObserverRetryCount++

            if (this.isDesktop.matches) {
                setTimeout(() => {
                    this.initializeIntersectionObserver()
                }, this.initializeObserverDelay)
            } else {
                this.initializeIntersectionObserver()
            }

            // Do not continue to avoid errors
            return
        }

        this.requestedAnimationFrames = []
        this.intersectionObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.captureFlipFrom()
                    this.requestedAnimationFrames.push(window.requestAnimationFrame(() => {
                        this.root.classList.remove('is-scrolled')
                        this.root.classList.add('is-calculating-expansion')
                        this.calculateFlipTo()
                        this.root.classList.add('is-scrolled')
                        this.root.classList.remove('is-calculating-expansion')
                        this.requestedAnimationFrames.push(window.requestAnimationFrame(() => {
                            this.root.classList.add('is-animated')
                            this.root.classList.remove('is-scrolled')
                            this.animateElements()
                        }))
                    }))
                } else {
                    this.captureFlipFrom()
                    this.requestedAnimationFrames.push(window.requestAnimationFrame(() => {
                        this.root.classList.add('is-scrolled', 'is-calculating-collapse')
                        this.calculateFlipTo()
                        this.root.classList.remove('is-scrolled', 'is-calculating-collapse')
                        this.requestedAnimationFrames.push(window.requestAnimationFrame(() => {
                            this.root.classList.add('is-animated')
                            this.root.classList.add('is-scrolled')
                            this.animateElements()
                        }))
                    }))
                }
            })
        }, {
            threshold: (innerHeaderHeight / detectorHeight)
        })
        this.intersectionObserver.observe(this.scrollDetector)
    }

    captureFlipFrom() {
        const elements = [this.root, this.mainMenu, this.metaMenu, this.headerSearch]
        if (this.languageMenu) {
            elements.push(this.languageMenu)
        }

        elements.forEach(element => {
            element.flip = {
                first: element.getBoundingClientRect()
            }
        })
    }

    calculateFlipTo() {
        const rootLast = this.root.getBoundingClientRect()
        const rootDeltaY = this.root.flip.first.top - rootLast.top
        const paddingDeltaY = rootDeltaY > 0 ? 22 : 0

        const menuLast = this.mainMenu.getBoundingClientRect()
        this.mainMenu.flip.delta = {
            x: this.mainMenu.flip.first.left - menuLast.left,
            y: this.mainMenu.flip.first.top - menuLast.top - rootDeltaY + (rootDeltaY > 0 ? 22 : -22)
        }

        const metaLast = this.metaMenu.getBoundingClientRect()
        this.metaMenu.flip.delta = {
            x: this.metaMenu.flip.first.left - metaLast.left,
            y: this.metaMenu.flip.first.top - metaLast.top - rootDeltaY
        }

        if (this.languageMenu) {
            const languageLast = this.languageMenu.getBoundingClientRect()
            this.languageMenu.flip.delta = {
                x: this.languageMenu.flip.first.left - languageLast.left,
                y: this.languageMenu.flip.first.top - languageLast.top - rootDeltaY
            }
        }

        const headerSearchLast = this.headerSearch.getBoundingClientRect()
        this.headerSearch.flip.delta = {
            x: this.headerSearch.flip.first.left - headerSearchLast.left,
            y: this.headerSearch.flip.first.top - headerSearchLast.top - rootDeltaY + paddingDeltaY
        }
    }

    animateElements() {
        const elements = [this.mainMenu, this.metaMenu, this.headerSearch]
        if (this.languageMenu) {
            elements.push(this.languageMenu)
        }

        elements.forEach(element => {
            const delta = element.flip.delta

            const animation = element.animate([
                {
                    transformOrigin: 'top left',
                    transform: `translate(${delta.x}px, ${delta.y}px)`
                },
                {
                    transformOrigin: 'top left',
                    transform: 'none'
                }
            ], {
                duration: 300,
                easing: 'ease-in-out',
                fill: 'forwards'
            })
            animation.addEventListener('finish', () => {
                animation.cancel()
                this.mainMenu.dispatchEvent(new Event('headerAnimated'))
            })
        })
    }

    open() {
        if (this.root.classList.contains('is-open')) {
            return false
        }

        this.root.classList.add('is-open')

        return true
    }

    close() {
        this.root.classList.remove('is-open')
    }

    isOpen() {
        return this.root.classList.contains('is-open')
    }
}

export { Header }
